import type { FormlyFieldConfig } from '@ngx-formly/core';
import { get } from 'lodash-es';
import { DateTime } from 'luxon';
import { findInFieldTree } from '../formly-fields/field-tree.util';
import { flaggedForRole } from '../../utils/editable-fields.util';
import {
  SummaryFormatEnum,
  type SummaryRow,
  type SummaryRowConfig,
  type SummarySettings,
} from './create-form-summary.types';

/** What a row shows while its field holds nothing yet. */
export const SUMMARY_EMPTY = '—';

/** The card's date line while no date is chosen (Figma: "Date • Week"). */
export const SUMMARY_SUBTITLE_PLACEHOLDER = 'Datum • Week';

/** Indexed like Date.getDay() and the cron field's days-of-week values ('0' = Sunday). */
const WEEKDAY_SHORT = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

type Model = Record<string, unknown>;

const isEmpty = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length === 0);

/** The generated field that writes `path` (a model path without `entry.`), searched across keyless sections. */
export function findFieldForPath(fields: FormlyFieldConfig[] | undefined, path: string): FormlyFieldConfig | undefined {
  return findInFieldTree(fields, field => field.props?.['keyPath'] === `entry.${path}`);
}

/**
 * Whether the field behind `path` is hidden for these roles: the per-role "Verbergen"
 * (`hideUserRole`) on the field itself or on a block around it. The form hides those by CSS only, so
 * prefill or a default can still fill the value, and the card must not read it out. Same pre-order
 * walk as `findFieldForPath`; `undefined` when no field writes `path`.
 */
function hiddenForRole(
  fields: FormlyFieldConfig[] | undefined,
  path: string,
  roleIds: readonly string[],
  hiddenAbove = false
): boolean | undefined {
  for (const field of Array.isArray(fields) ? fields : []) {
    if (!field || typeof field !== 'object') continue;
    const hidden = hiddenAbove || flaggedForRole(field.props?.['hideUserRole'], roleIds);
    if (field.props?.['keyPath'] === `entry.${path}`) return hidden;
    const nested = hiddenForRole(field.fieldGroup, path, roleIds, hidden);
    if (nested !== undefined) return nested;
  }
  return undefined;
}

/** A stored linked entry, option object or plain value as one line of text. */
export function textOf(value: unknown, field?: FormlyFieldConfig): string {
  if (isEmpty(value)) return '';
  if (Array.isArray(value)) {
    return value
      .map(item => textOf(item, field))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nee';
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const entry = record['entry'] as Record<string, unknown> | undefined;
    const text = record['selectedInput'] ?? entry?.['name'] ?? record['label'] ?? record['name'];
    return typeof text === 'string' || typeof text === 'number' ? String(text) : '';
  }
  // A select or radio stores the option value; the planner reads its label.
  const options = field?.props?.options;
  const option = Array.isArray(options)
    ? (options as { value?: unknown; label?: unknown }[]).find(o => o?.value === value)
    : undefined;
  return option?.label !== undefined ? String(option.label) : String(value);
}

/** A stored ISO date as a valid DateTime, or null. */
function parseDate(value: unknown): DateTime | null {
  // A date part is required: Luxon reads a time-only '08:00' as today, which would show a date
  // the planner never chose.
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  const date = DateTime.fromISO(value);
  return date.isValid ? date : null;
}

function dateOf(value: unknown): string {
  return parseDate(value)?.toFormat('dd-MM-yyyy') ?? '';
}

/** `Wekelijks · ma, wo` from the cron field's `repeat` object. */
function repeatOf(value: unknown): string {
  const repeat = value as { repeat?: unknown; 'days-of-week'?: unknown } | null | undefined;
  // 'no-repeat' is the cron field's own "not repeating" default, not a frequency.
  if (!repeat || typeof repeat.repeat !== 'string' || !repeat.repeat || repeat.repeat === 'no-repeat') return '';
  const days = Array.isArray(repeat['days-of-week'])
    ? [...repeat['days-of-week']]
        // '' and null would become 0 (Sunday) through Number().
        .filter(day => (typeof day === 'string' ? day.trim() !== '' : typeof day === 'number'))
        .map(Number)
        .filter(day => Number.isInteger(day) && day >= 0 && day <= 6)
        // Monday first, the way a planner reads a week.
        .sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
        .map(day => WEEKDAY_SHORT[day])
    : [];
  return days.length ? `${repeat.repeat} · ${days.join(', ')}` : repeat.repeat;
}

/**
 * `<departure> → <destination>, n stops`. The row's path is the route field (the stop list); the
 * departure and destination are its siblings, under the keys the route field itself is configured
 * with (`departureLocationField` / `destinationLocationField`, last segment), defaulting the same way.
 */
function routeOf(model: Model, row: SummaryRowConfig, field?: FormlyFieldConfig): string {
  const parent = row.path.includes('.') ? row.path.slice(0, row.path.lastIndexOf('.')) : '';
  const sibling = (configured: unknown, fallback: string): unknown => {
    const key = typeof configured === 'string' && configured ? configured.split('.').pop()! : fallback;
    return get(model, parent ? `${parent}.${key}` : key);
  };
  const departure = textOf(sibling(field?.props?.['departureLocationField'], 'departure-location'));
  const destination = textOf(sibling(field?.props?.['destinationLocationField'], 'destination-location'));
  const stopsValue = get(model, row.path);
  // The route starts with one blank stop row; only stops with a location count.
  const stops = Array.isArray(stopsValue)
    ? stopsValue.filter(stop => !isEmpty((stop as Record<string, unknown> | null)?.['location'])).length
    : 0;
  if (!departure && !destination && !stops) return '';
  const ends = `${departure || SUMMARY_EMPTY} → ${destination || SUMMARY_EMPTY}`;
  return stops ? `${ends}, ${stops} ${stops === 1 ? 'stop' : 'stops'}` : ends;
}

/**
 * One summary row's text. `field` is the generated field behind `row.path` (option labels, route
 * keys); `fallbackField` the one behind `row.fallbackPath`. Empty → `—`.
 */
export function summaryValue(
  model: Model,
  row: SummaryRowConfig,
  field?: FormlyFieldConfig,
  fallbackField?: FormlyFieldConfig
): string {
  const value = get(model, row.path);
  let text: string;
  switch (row.format) {
    case SummaryFormatEnum.Date:
      text = dateOf(value);
      break;
    case SummaryFormatEnum.Route:
      text = routeOf(model, row, field);
      break;
    case SummaryFormatEnum.Repeat:
      text = repeatOf(value);
      break;
    default:
      text = textOf(value, field);
  }
  if (!text && row.fallbackPath) {
    text = textOf(get(model, row.fallbackPath), fallbackField);
  }
  return text || SUMMARY_EMPTY;
}

/** `24-10-2026 • Week 43`, or '' when the path holds no valid date. */
export function summarySubtitle(model: Model, path: string | undefined): string {
  const date = path ? parseDate(get(model, path)) : null;
  // ISO week, as the planning boards number them.
  return date ? `${date.toFormat('dd-MM-yyyy')} • Week ${date.weekNumber}` : '';
}

/** A configured row with the generated fields behind its paths, looked up once per form. */
export interface ResolvedSummaryRow {
  row: SummaryRowConfig;
  field?: FormlyFieldConfig;
  fallbackField?: FormlyFieldConfig;
}

/**
 * Pairs every row with its fields. The field tree does not change while the form is open. A row
 * whose field is hidden for the user's roles is left out; a hidden fallback is dropped.
 */
export function resolveSummaryRows(
  settings: SummarySettings,
  fields: FormlyFieldConfig[] | undefined,
  roleIds: readonly string[] = []
): ResolvedSummaryRow[] {
  return (settings.rows ?? [])
    .filter(row => !hiddenForRole(fields, row.path, roleIds))
    .map(row => {
      const fallbackPath =
        row.fallbackPath && !hiddenForRole(fields, row.fallbackPath, roleIds) ? row.fallbackPath : undefined;
      return {
        row: { ...row, fallbackPath },
        field: findFieldForPath(fields, row.path),
        fallbackField: fallbackPath ? findFieldForPath(fields, fallbackPath) : undefined,
      };
    });
}

/** Every resolved row, rendered against the current model. */
export function buildSummaryRows(model: Model, rows: ResolvedSummaryRow[]): SummaryRow[] {
  return rows.map(({ row, field, fallbackField }) => ({
    label: row.label,
    value: summaryValue(model, row, field, fallbackField),
  }));
}
